// ─────────────────────────────────────────────────────────────────────────────────────────────
// despiece.js — LISTADO DE MATERIALES (BOM) de la construcción fotovoltaica.
//
// Módulo PURO (sin red, sin React, sin imports relativos → vive en `src/motores/` y el backend lo
// carga por `import()` dinámico igual que el resto de los motores).
//
// 🔴 QUÉ PROBLEMA RESUELVE. Hasta ahora el presupuesto se armaba con GLOBALES:
// `estructura × panel`, `cableado × kWp`, `protecciones × kWp`. Eso alcanza para cotizar, pero NO
// para construir: nadie puede ir a comprar con "cableado: $X por kWp". Una obra necesita metros de
// cable por calibre, número de clamps, cuántos fusibles, cuántas varillas. Este módulo produce ESO.
//
// 🔴 LA REGLA QUE GOBIERNA TODO EL ARCHIVO: cada renglón declara DE DÓNDE salió.
//   · 'calculado' — se deriva del diseño sin ninguna suposición (módulos, inversores, fusibles).
//   · 'derivado'  — se deriva de la geometría del arreglo bajo una hipótesis de montaje declarada.
//   · 'supuesto'  — depende de una distancia o condición que solo se conoce midiendo en sitio.
// Un despiece que mezcla lo medido con lo supuesto sin distinguirlos hace que el instalador compre
// de menos (y pare la obra) o de más (y se coma el margen), sin manera de saber cuál de los dos
// pasó. Por eso `supuestos[]` viaja con el resultado y los renglones supuestos se marcan.
//
// FORMA DE SALIDA: alineada con el `bill_of_materials` de los formatos de intercambio de la
// industria (tipo · sku · nombre · fabricante · cantidad) para que exportar sea un `map`, no un
// traductor. Se agrega `unidad`, `fuente` y `nota`, que esos formatos no llevan y esta obra sí
// necesita — más `certRetie`, que ningún formato extranjero contempla y en Colombia decide si la
// instalación se puede energizar.
//
// 🇨🇴 CERTIFICADO DE PRODUCTO RETIE. El RETIE (Res. 40117/2024, Libro 2) exige certificado de
// conformidad de producto para módulos ≥ 100 W, inversores, baterías, reguladores, conductores FV,
// DPS y protecciones. Un material sin certificado NO es un ahorro: es una instalación que el
// inspector no aprueba y una obra que se rehace. Por eso el certificado es una COLUMNA del
// despiece, con su artículo — la orden de compra tiene que salir sabiéndolo, no descubrirlo en la
// visita de inspección.

/** Tipos de componente. Espejo del vocabulario de intercambio de la industria. */
export const TIPO = {
  MODULO: 'modules',
  INVERSOR: 'inverters',
  ESTRUCTURA: 'racking',
  CABLE_DC: 'dc_wiring',
  CABLE_AC: 'ac_wiring',
  CONECTOR: 'connectors',
  PROTECCION: 'protections',
  TIERRA: 'grounding',
  CANALIZACION: 'conduit',
  TABLERO: 'panelboards',
};

/**
 * Distancias por defecto (metros). Se usan SOLO si el llamador no las mide, y cuando se usan el
 * resultado lo DICE. No son un dato del proyecto: son un punto de partida para que el despiece
 * no salga con huecos, sujeto a verificación en sitio.
 */
export const DISTANCIAS_DEFECTO = {
  // Del arreglo al inversor. Escala con el tamaño porque una planta grande no cabe al lado del
  // inversor: un arreglo de 100 kWp no tiene el mismo recorrido que uno de 5 kWp.
  arregloInversor: null, // se calcula por tamaño abajo
  inversorTablero: 10,
  tableroMedidor: 8,
};

/** Recorrido típico arreglo→inversor según potencia. Declarado, no escondido. */
function distanciaArregloPorTamano(kwp) {
  if (kwp <= 5) return 12;
  if (kwp <= 20) return 20;
  if (kwp <= 100) return 40;
  return 80;
}

/**
 * Redondeo hacia arriba a múltiplo (el cable se compra en rollos, no al metro exacto).
 *
 * ⚠️ El `EPS` no es cosmético: `100 * 1.1` en punto flotante da `110.00000000000001`, y sin
 * tolerancia ese residuo empuja el resultado al rollo SIGUIENTE — el despiece pediría 115 m donde
 * caben 110. Cinco metros de más por ruido numérico, en cada renglón de cable, es exactamente la
 * clase de imprecisión que le quita credibilidad a una orden de compra.
 */
function aMultiplo(v, mult) {
  const EPS = 1e-9;
  if (!(mult > 0)) return Math.ceil(v - EPS);
  return Math.ceil(v / mult - EPS) * mult;
}

function num(v, porDefecto = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : porDefecto;
}

/**
 * Calcula el listado de materiales.
 *
 * @param {object} o
 *  - numPanels, ns (nº de strings), ppss (módulos por string), actKwp
 *  - panel {manufacturer, model, watts, length_m, width_m, isc}
 *  - inverter {manufacturer, model, kw, mppt_count}, numInv
 *  - orientacion 'portrait' | 'landscape'
 *  - modulosPorFila (opcional) — si no viene, se asume una fila por string y se DECLARA
 *  - calibres {dc, ac, tierra} — los AWG ya seleccionados por la memoria (no se recalculan acá:
 *    duplicar la tabla de ampacidad sería una segunda fuente de verdad para el mismo número)
 *  - protAmps, mainBreakerAmps — protecciones ya dimensionadas
 *  - distancias {arregloInversor, inversorTablero, tableroMedidor} — en metros, si se midieron
 *  - montaje 'techo' | 'piso'
 * @returns {{items: Array, supuestos: string[], resumen: object}}
 */
export function calcularDespiece(o = {}) {
  const items = [];
  const supuestos = [];

  const numPanels = Math.max(0, Math.round(num(o.numPanels)));
  const ns = Math.max(0, Math.round(num(o.ns)));
  const ppss = Math.max(0, Math.round(num(o.ppss)));
  const kwp = num(o.actKwp);
  const panel = o.panel || {};
  const inv = o.inverter || {};
  const numInv = Math.max(0, Math.round(num(o.numInv)));
  const montajePiso = o.montaje === 'piso';

  const add = (it) => { if (it.cantidad > 0) items.push(it); };

  // ── 1. Módulos e inversores: exactos, sin ninguna hipótesis ────────────────────────────────
  const wp = num(panel.watts);
  add({
    tipo: TIPO.MODULO, sku: panel.sku || null,
    nombre: panel.model || panel.name || 'Módulo fotovoltaico',
    fabricante: panel.manufacturer || null,
    cantidad: numPanels, unidad: 'und', fuente: 'calculado',
    // El umbral de 100 W es literal del RETIE: por debajo, y fuera de red de uso general, no exige
    // certificado de producto. Todo módulo de un sistema conectado lo supera holgadamente.
    certRetie: wp === 0 || wp >= 100, articulo: 'RETIE Art. 2.3.25 / 3.17.23',
    nota: wp ? `${wp} Wp` : null,
  });
  add({
    tipo: TIPO.INVERSOR, sku: inv.sku || null,
    nombre: inv.model || inv.name || 'Inversor',
    fabricante: inv.manufacturer || null,
    cantidad: numInv, unidad: 'und', fuente: 'calculado',
    certRetie: true, articulo: 'RETIE Art. 2.3.23 / 3.17.21 · IEC 62109-1/-2 · IEC 62116',
    nota: inv.kw ? `${inv.kw} kW` : null,
  });

  // ── 2. Estructura: derivada de la GEOMETRÍA del arreglo ────────────────────────────────────
  // El número de filas y de módulos por fila determina metros de riel y número de clamps. Sin el
  // layout real del techo se asume UNA fila por string — es lo más común en cubierta y lo dice.
  const modulosPorFila = Math.max(1, Math.round(num(o.modulosPorFila, ppss || numPanels || 1)));
  const filas = modulosPorFila > 0 ? Math.ceil(numPanels / modulosPorFila) : 0;
  if (!o.modulosPorFila && filas > 0) {
    supuestos.push(
      `Layout de montaje: ${filas} fila(s) de ${modulosPorFila} módulo(s). Se asumió una fila por ` +
      `string; con el layout real de la cubierta cambian los metros de riel y el número de clamps.`
    );
  }

  // En montaje vertical (portrait) los rieles corren a lo ANCHO del módulo; en horizontal, a lo
  // largo. Dos rieles por fila (norma de fabricante para módulos de 2 apoyos).
  const portrait = (o.orientacion || 'portrait') !== 'landscape';
  const pasoM = portrait ? num(panel.width_m, 1.13) : num(panel.length_m, 2.28);
  const holguraM = 0.02; // separación entre módulos
  const largoFilaM = modulosPorFila * (pasoM + holguraM);
  const rielM = filas * largoFilaM * 2;
  add({
    tipo: TIPO.ESTRUCTURA, sku: null, nombre: 'Riel de aluminio para montaje FV',
    fabricante: null, cantidad: aMultiplo(rielM * 1.05, 1), unidad: 'm', fuente: 'derivado',
    nota: `${filas} fila(s) × ${largoFilaM.toFixed(2)} m × 2 rieles, +5% de desperdicio de corte`,
  });

  // Clamps: por fila, 4 terminales (2 por riel en los extremos) y 2×(n−1) intermedios.
  add({
    tipo: TIPO.ESTRUCTURA, sku: null, nombre: 'Clamp terminal (end clamp)', fabricante: null,
    cantidad: filas * 4, unidad: 'und', fuente: 'derivado', nota: '2 por riel en cada extremo de fila',
  });
  add({
    tipo: TIPO.ESTRUCTURA, sku: null, nombre: 'Clamp intermedio (mid clamp)', fabricante: null,
    cantidad: filas * 2 * Math.max(0, modulosPorFila - 1), unidad: 'und', fuente: 'derivado',
    nota: 'Cada clamp intermedio sujeta dos módulos contiguos',
  });

  // Anclajes a la cubierta: uno cada ~1.2 m de riel (criterio de carga de viento; en piso, hincado).
  const anclajePorM = 1.2;
  add({
    tipo: TIPO.ESTRUCTURA, sku: null,
    nombre: montajePiso ? 'Poste/hinca de estructura en piso' : 'Anclaje a cubierta con sello (L-feet / tornillo estructural)',
    fabricante: null, cantidad: Math.ceil(rielM / anclajePorM), unidad: 'und', fuente: 'derivado',
    nota: `Un apoyo cada ${anclajePorM} m de riel. La separación FINAL la fija el cálculo de carga de viento del sitio (NSR-10 Título B).`,
  });
  if (!montajePiso) {
    supuestos.push(
      'Anclajes a cubierta: la cantidad y el tipo dependen del material y la estructura de la ' +
      'cubierta (teja, lámina, concreto), que se define en la visita técnica.'
    );
  }

  // ── 3. Cable DC ────────────────────────────────────────────────────────────────────────────
  const dist = o.distancias || {};
  const dcMedido = num(dist.arregloInversor) > 0;
  const dArregloInv = dcMedido
    ? num(dist.arregloInversor)
    : distanciaArregloPorTamano(kwp);
  if (!dcMedido) {
    supuestos.push(
      `Recorrido arreglo→inversor: ${dArregloInv} m (estimado por potencia). Es la variable que más ` +
      `mueve el metraje de cable solar y la caída de tensión — medir en sitio.`
    );
  }
  // Ida y vuelta (+ y −) por cada string. Los latiguillos del propio módulo ya vienen de fábrica.
  const cableDcM = ns * dArregloInv * 2;
  add({
    tipo: TIPO.CABLE_DC, sku: null,
    nombre: `Cable solar DC ${o.calibres?.dc ? `${o.calibres.dc} AWG` : ''} (H1Z2Z2-K / PV1-F, 1500 V)`.replace(/\s+/g, ' ').trim(),
    fabricante: null, cantidad: aMultiplo(cableDcM * 1.1, 5), unidad: 'm',
    // Medido en la visita → derivado de un dato del sitio. Sin medición, supuesto.
    fuente: dcMedido ? 'derivado' : 'supuesto',
    certRetie: true, articulo: 'RETIE Libro 2 · certificado para uso FV (UL 4703 o equivalente)',
    nota: `${ns} string(s) × ${dArregloInv} m × 2 polos, +10% de holgura. Rollo de 5 m. Resistente a intemperie/UV.`,
  });
  add({
    tipo: TIPO.CONECTOR, sku: null, nombre: 'Par de conectores tipo MC4 (macho + hembra)',
    fabricante: null, cantidad: ns * 2, unidad: 'par', fuente: 'calculado',
    certRetie: true, articulo: 'RETIE Art. 3.17.23',
    nota: 'Un par por polaridad y por string. Polarizados, enclavables, IP a prueba de agua y marcados «No desconectar bajo carga». Con arneses pre-terminados la cantidad baja.',
  });

  // ── 4. Cable AC y canalización ─────────────────────────────────────────────────────────────
  const fases = num(inv.phases, 1);
  const hilosAc = fases >= 3 ? 4 : fases === 2 ? 3 : 2; // + neutro
  const acInvTabMedido = num(dist.inversorTablero) > 0;
  const acTabMedMedido = num(dist.tableroMedidor) > 0;
  const acMedido = acInvTabMedido && acTabMedMedido;
  const dInvTab = acInvTabMedido ? num(dist.inversorTablero) : DISTANCIAS_DEFECTO.inversorTablero;
  const dTabMed = acTabMedMedido ? num(dist.tableroMedidor) : DISTANCIAS_DEFECTO.tableroMedidor;
  if (!acMedido) {
    supuestos.push(
      `Recorridos AC: inversor→tablero ${dInvTab} m, tablero→medidor ${dTabMed} m (valores de ` +
      `referencia). Verificar en sitio: definen el calibre AC por caída de tensión.`
    );
  }
  const cableAcM = (dInvTab + dTabMed) * hilosAc;
  add({
    tipo: TIPO.CABLE_AC, sku: null,
    nombre: `Conductor AC Cu THHN/THWN-2 ${o.calibres?.ac ? `${o.calibres.ac} AWG` : ''}`.replace(/\s+/g, ' ').trim(),
    fabricante: null, cantidad: aMultiplo(cableAcM * 1.1, 5), unidad: 'm',
    fuente: acMedido ? 'derivado' : 'supuesto',
    nota: `${hilosAc} hilos (${fases >= 3 ? '3F+N' : fases === 2 ? '2F+N' : 'F+N'}) × ${dInvTab + dTabMed} m, +10%.`,
  });
  add({
    tipo: TIPO.CANALIZACION, sku: null, nombre: 'Tubería conduit EMT/PVC + accesorios',
    fabricante: null, cantidad: aMultiplo((dArregloInv + dInvTab + dTabMed) * 1.1, 3), unidad: 'm',
    // El conduit recorre DC + AC: solo deja de ser supuesto cuando se midieron los tres tramos.
    fuente: dcMedido && acMedido ? 'derivado' : 'supuesto',
    nota: 'Recorrido total DC + AC, +10%. Diámetro según factor de relleno (NTC 2050 Cap. 9).',
  });

  // ── 5. Protecciones ────────────────────────────────────────────────────────────────────────
  // NTC 2050 §690.9: la protección de sobrecorriente de string se exige cuando hay 3 o más strings
  // en paralelo sobre el mismo bus — con 1 o 2 la corriente de retorno no alcanza el límite del
  // conductor. Por eso NO se ponen fusibles siempre: ponerlos de más también es un error de diseño.
  const mpptTotales = Math.max(1, num(inv.mppt_count, 1) * Math.max(1, numInv));
  const stringsPorMppt = mpptTotales > 0 ? Math.ceil(ns / mpptTotales) : ns;
  if (stringsPorMppt >= 3) {
    const iFusible = panel.isc ? Math.ceil(num(panel.isc) * 1.56) : null;
    add({
      tipo: TIPO.PROTECCION, sku: null,
      nombre: `Fusible de string gPV 1000 V${iFusible ? ` — ${iFusible} A` : ''}`,
      fabricante: null, cantidad: ns * 2, unidad: 'und', fuente: 'calculado', certRetie: true,
      articulo: 'NTC 2050 §690.9',
      nota: `${stringsPorMppt} strings en paralelo por MPPT ⇒ exigido por NTC 2050 §690.9. Uno por polaridad. Corriente ≥ 1.56 × Isc.`,
    });
    add({
      tipo: TIPO.PROTECCION, sku: null, nombre: 'Portafusible DC 1000 V', fabricante: null,
      cantidad: ns * 2, unidad: 'und', fuente: 'calculado', certRetie: true, nota: null,
    });
  } else {
    supuestos.push(
      `Sin fusibles de string: ${stringsPorMppt} string(s) en paralelo por MPPT — por debajo del ` +
      `umbral de NTC 2050 §690.9, que los exige a partir de 3.`
    );
  }
  add({
    tipo: TIPO.PROTECCION, sku: null, nombre: 'Interruptor-seccionador DC bajo carga', fabricante: null,
    cantidad: numInv, unidad: 'und', fuente: 'calculado', certRetie: true,
    articulo: 'NTC 2050 §690.13 / §690.15', nota: 'Medio de desconexión DC, accesible e identificado',
  });
  add({
    tipo: TIPO.PROTECCION, sku: null, nombre: 'DPS DC Tipo 2 (1000 V)', fabricante: null,
    cantidad: numInv, unidad: 'und', fuente: 'calculado', certRetie: true,
    articulo: 'RETIE Título 13', nota: 'Protección contra sobretensiones lado DC',
  });
  const brkAc = num(o.mainBreakerAmps) || num(o.protAmps);
  add({
    tipo: TIPO.PROTECCION, sku: null,
    nombre: `Interruptor termomagnético AC${brkAc ? ` ${brkAc} A` : ''} ${fases >= 3 ? '3P' : fases === 2 ? '2P' : '1P'}`,
    fabricante: null, cantidad: numInv, unidad: 'und', fuente: 'calculado', certRetie: true,
    articulo: 'NTC 2050 §690.9 / §705.12',
    nota: 'Debe ser capaz de interrumpir la corriente de cortocircuito máxima del punto de conexión (RETIE Art. 3.18.2)',
  });
  add({
    tipo: TIPO.PROTECCION, sku: null, nombre: 'DPS AC Tipo 2', fabricante: null,
    cantidad: 1, unidad: 'und', fuente: 'calculado', certRetie: true,
    articulo: 'RETIE Título 13 — sobretensiones transitorias',
    nota: 'Coordinado con el sistema de puesta a tierra y la evaluación de riesgo de rayos.',
  });
  add({
    tipo: TIPO.TABLERO, sku: null, nombre: 'Tablero de conexión FV (gabinete IP65 + barraje)',
    fabricante: null, cantidad: 1, unidad: 'und', fuente: 'calculado', certRetie: false, nota: null,
  });

  // ── 5-bis. Funciones de seguridad EXIGIDAS que suelen venir dentro del inversor ─────────────
  // No son opcionales y no siempre son un equipo aparte: si el inversor las trae certificadas, el
  // renglón se cumple con él. Aparecen igual en el despiece porque lo que el inspector verifica es
  // la FUNCIÓN, no el gabinete — y si el inversor elegido no la trae, hay que comprarla.
  const vdcSistema = num(o.vdcMaxSistema);
  const esOnGrid = (o.systemType || 'on-grid') !== 'off-grid';
  if (esOnGrid && (vdcSistema === 0 || vdcSistema > 80)) {
    add({
      tipo: TIPO.PROTECCION, sku: null,
      nombre: 'Protección contra falla de arco FV (AFCI)', fabricante: null,
      cantidad: numInv, unidad: 'función', fuente: 'calculado', certRetie: true,
      articulo: 'RETIE Art. 3.17.23 · NTC 2050 §690.11',
      nota: `Exigida en sistemas conectados a red con tensión DC > 80 V${vdcSistema ? ` (este sistema: ${Math.round(vdcSistema)} V)` : ''}. Se cumple con la función integrada del inversor si está certificada; si no, con equipo dedicado.`,
    });
  }
  add({
    tipo: TIPO.PROTECCION, sku: null,
    nombre: 'Protección/detección de falla a tierra DC (GFDI)', fabricante: null,
    cantidad: numInv, unidad: 'función', fuente: 'calculado', certRetie: true,
    articulo: 'NTC 2050 §690.41(B) / §690.42',
    nota: 'Habitualmente integrada en el inversor. Verificar en la ficha técnica antes de comprar.',
  });

  // ── 5-ter. Rotulado y señalización: material barato que reprueba la inspección si falta ─────
  add({
    tipo: TIPO.TABLERO, sku: null,
    nombre: 'Placa de identificación del sistema FV (grabada, permanente)', fabricante: null,
    cantidad: 1, unidad: 'und', fuente: 'calculado', certRetie: false,
    articulo: 'RETIE Art. 3.17.23',
    nota: 'Va en la zona de desconexión y debe indicar: tensión nominal CA, corriente máxima CA, potencia máxima CA, tensión máxima CC y corriente de cortocircuito máxima CC. Todos esos valores salen de esta misma memoria.',
  });
  // Un rótulo cada ~3 m de canalización DC más uno por caja/gabinete.
  const canalDcM = dArregloInv * 1.1;
  add({
    tipo: TIPO.TABLERO, sku: null,
    nombre: 'Rótulo permanente «ADVERTENCIA: FUENTE DE ALIMENTACIÓN FOTOVOLTAICA»', fabricante: null,
    cantidad: Math.max(2, Math.ceil(canalDcM / 3) + numInv + 1), unidad: 'und', fuente: 'derivado',
    articulo: 'RETIE Art. 3.17.23',
    nota: 'En canalizaciones, cajas y gabinetes con circuitos DC. Uno cada 3 m de recorrido más uno por equipo.',
  });
  add({
    tipo: TIPO.PROTECCION, sku: null,
    nombre: 'Señalización de medio de desconexión FV', fabricante: null,
    cantidad: 1, unidad: 'und', fuente: 'calculado', certRetie: false,
    articulo: 'NTC 2050 §690.13 · RETIE Art. 3.17.23',
    nota: 'El medio de desconexión debe estar en zona identificada y de fácil acceso.',
  });

  // ── 6. Puesta a tierra ─────────────────────────────────────────────────────────────────────
  add({
    tipo: TIPO.TIERRA, sku: null, nombre: 'Varilla copperweld 5/8" × 2.4 m', fabricante: null,
    cantidad: 1, unidad: 'und', fuente: 'supuesto', certRetie: false,
    articulo: 'NTC 2050 §250.52(A)(5) · RETIE Libro 3 Título 12',
    nota: 'Cantidad mínima. Si la resistividad del terreno da > 25 Ω se agregan varillas en paralelo (NTC 2050 §250.53).',
  });
  supuestos.push(
    'Puesta a tierra: la cantidad final de electrodos depende de la MEDICIÓN de resistividad del ' +
    'terreno. Una sola varilla es el mínimo normativo, no una garantía de cumplir los 25 Ω.'
  );
  // El conductor de tierra recorre las filas (unión de estructura) y baja al electrodo.
  const tierraM = rielM * 0.6 + dArregloInv + dInvTab;
  add({
    tipo: TIPO.TIERRA, sku: null,
    nombre: `Conductor de puesta a tierra Cu ${o.calibres?.tierra ? `${o.calibres.tierra} AWG` : ''} (desnudo/verde)`.replace(/\s+/g, ' ').trim(),
    fabricante: null, cantidad: aMultiplo(tierraM * 1.1, 5), unidad: 'm', fuente: 'supuesto',
    certRetie: false, articulo: 'NTC 2050 §690.45 / §690.47',
    nota: 'Cobre aislado color verde o desnudo. Unión equipotencial de estructuras + bajante al electrodo (NTC 2050 §690.47).',
  });
  add({
    tipo: TIPO.TIERRA, sku: null, nombre: 'Conector de puesta a tierra para módulo/estructura (WEEB o similar)',
    fabricante: null, cantidad: numPanels + filas, unidad: 'und', fuente: 'derivado',
    nota: 'Uno por módulo + uno por fila para continuidad de la estructura.',
  });
  add({
    tipo: TIPO.TIERRA, sku: null, nombre: 'Soldadura exotérmica o conector de compresión certificado',
    fabricante: null, cantidad: 2, unidad: 'und', fuente: 'calculado', nota: 'Unión conductor–electrodo',
  });

  const porFuente = items.reduce((acc, it) => {
    acc[it.fuente] = (acc[it.fuente] || 0) + 1;
    return acc;
  }, {});

  return {
    items,
    supuestos,
    resumen: {
      renglones: items.length,
      porFuente,
      filas,
      modulosPorFila,
      rielM: Math.round(rielM),
      // Cuántos renglones NO se pueden comprar sin verificar en sitio. Es el número que le dice al
      // instalador cuánta visita técnica falta antes de emitir la orden de compra.
      requierenSitio: items.filter((i) => i.fuente === 'supuesto').length,
      // Cuántos renglones exigen certificado de producto RETIE. Es lo que el comprador tiene que
      // pedirle al proveedor ANTES de la orden — no en la inspección.
      conCertRetie: items.filter((i) => i.certRetie).length,
    },
  };
}

/**
 * CHECKLIST DE CUMPLIMIENTO RETIE — derivado del diseño, no una plantilla en blanco.
 *
 * Un checklist genérico lo llena cualquiera con todo en "sí". Este se DERIVA: cada ítem sabe si el
 * diseño ya lo resuelve (`estado: 'cumple'`), si depende de una medición o documento que todavía no
 * existe (`'pendiente'`), o si no aplica a este sistema (`'no_aplica'`, con el motivo). Lo que el
 * diseño no puede saber solo —la resistencia real del terreno, el certificado que emitió el
 * proveedor, la matrícula del profesional— se marca pendiente en vez de darse por bueno.
 *
 * @returns {Array<{item, estado, referencia, detalle}>}
 */
export function checklistRetie(o = {}, despiece = null) {
  const d = despiece || calcularDespiece(o);
  const kwp = num(o.actKwp);
  const vdc = num(o.vdcMaxSistema);
  const esOnGrid = (o.systemType || 'on-grid') !== 'off-grid';
  const conectadoRed = esOnGrid;
  const filas = [];
  const push = (item, estado, referencia, detalle) => filas.push({ item, estado, referencia, detalle });

  // — Diseño y documentación
  push('Diseño eléctrico detallado',
    (conectadoRed || kwp >= 10) ? 'requerido' : 'no_aplica',
    'RETIE Libro 3 Título 3',
    (conectadoRed || kwp >= 10)
      ? 'Exigido para AGPE/GD conectados a red y para sistemas aislados ≥ 10 kVA.'
      : `Sistema aislado de ${kwp} kWp: por debajo del umbral de 10 kVA para diseño detallado.`);
  push('Memorias de cálculo', 'cumple', 'RETIE Libro 3 Título 3',
    'Dimensionamiento de conductores, protecciones, caída de tensión y puesta a tierra en este documento.');
  push('Diagrama unifilar (DC + AC + protecciones + SPT)', 'pendiente', 'RETIE Libro 3 Título 3',
    'El unifilar DC se genera con el estudio; el plano de implantación y el de canalizaciones se levantan en la visita técnica.');
  push('Fichas técnicas de todos los equipos', 'pendiente', 'RETIE Libro 4',
    'Se anexan las hojas de datos de módulos, inversor, conductores y DPS.');
  push('Certificados de conformidad de producto RETIE', 'pendiente', 'RETIE Libro 2',
    `${d.resumen.conCertRetie} renglón(es) del despiece exigen certificado de producto. Solicitarlos al proveedor ANTES de la orden de compra.`);
  push('Declaración de cumplimiento del constructor/instalador', 'pendiente', 'RETIE Libro 4',
    'La firma el instalador al terminar la obra, con su matrícula profesional.');

  // — Requisitos físicos de la instalación
  push('Medio de desconexión FV accesible e identificado', 'cumple', 'NTC 2050 §690.13 · RETIE Art. 3.17.23',
    'Incluido en el despiece con su señalización.');
  push('Placa de identificación del sistema en la zona de desconexión', 'cumple', 'RETIE Art. 3.17.23',
    'Debe llevar tensión y corriente nominales CA, potencia máxima CA, tensión máxima CC y corriente de cortocircuito máxima CC.');
  push('Rotulado permanente de canalizaciones y cajas DC', 'cumple', 'RETIE Art. 3.17.23',
    '«ADVERTENCIA: FUENTE DE ALIMENTACIÓN FOTOVOLTAICA» — incluido en el despiece.');
  push('Separación de circuitos DC del FV respecto de circuitos no FV', 'requerido', 'RETIE Art. 3.17.23 · NTC 2050 §690.31',
    'Los circuitos DC no comparten canalización con circuitos ajenos al sistema FV ni con la salida del inversor, salvo barrera física.');
  push('Protección contra falla a tierra DC (GFDI)', 'cumple', 'NTC 2050 §690.41(B) / §690.42',
    'Normalmente integrada en el inversor: verificar en su ficha técnica.');
  push('Protección contra falla de arco (AFCI)',
    (conectadoRed && (vdc === 0 || vdc > 80)) ? 'cumple' : 'no_aplica',
    'NTC 2050 §690.11 · RETIE Art. 3.17.23',
    (conectadoRed && (vdc === 0 || vdc > 80))
      ? `Exigida por tensión DC > 80 V${vdc ? ` (${Math.round(vdc)} V)` : ''}. Se cumple con la función certificada del inversor.`
      : conectadoRed ? `Tensión DC del sistema ${Math.round(vdc)} V ≤ 80 V.` : 'Sistema no conectado a red.');
  push('Protección de sobrecorriente de string',
    d.items.some((i) => /Fusible de string/.test(i.nombre)) ? 'cumple' : 'no_aplica',
    'NTC 2050 §690.9',
    d.items.some((i) => /Fusible de string/.test(i.nombre))
      ? 'Fusibles gPV incluidos en el despiece.'
      : 'Menos de 3 strings en paralelo por MPPT: la norma no los exige.');
  push('Protección contra sobretensiones (DPS) DC y AC', 'cumple', 'RETIE Título 13',
    'DPS Tipo 2 en ambos lados, coordinados con la puesta a tierra.');

  // — Puesta a tierra: lo que NO se puede dar por cumplido desde el escritorio
  push('Sistema de puesta a tierra ≤ 25 Ω', 'pendiente', 'NTC 2050 §250.53(A)(2) · RETIE Libro 3 Título 12',
    'Solo se verifica MIDIENDO en sitio. El despiece incluye una varilla como mínimo normativo; si la medición supera 25 Ω se agregan electrodos.');
  push('Equipotencialización de estructuras y partes metálicas', 'cumple', 'NTC 2050 §690.47 · RETIE Libro 3 Título 12',
    'Conductor de tierra y conectores de módulo/estructura incluidos.');
  push('Evaluación de riesgo por descargas atmosféricas', 'pendiente', 'RETIE Título 13 · IEC 62305-2 / GTC-IEC-TR 63227',
    'Depende del nivel ceráunico del sitio. En zonas con isoceráunica > 60 días/año es obligatoria.');

  // — Estructura y conexión
  push('Cálculo estructural: cargas de viento, sismo y dilatación', 'pendiente', 'RETIE Art. 3.17.23 · NSR-10',
    'Define la separación real de anclajes; el despiece usa un criterio típico que hay que confirmar.');
  push('Interruptor capaz de interrumpir la Icc del punto de conexión',
    conectadoRed ? 'pendiente' : 'no_aplica', 'RETIE Art. 3.18.2',
    conectadoRed
      ? 'Requiere el nivel de cortocircuito que informa el operador de red en el punto de conexión.'
      : 'Sistema no conectado a red.');
  push('Coordinación de protecciones y tiempos de despeje',
    conectadoRed ? 'pendiente' : 'no_aplica', 'RETIE Art. 3.18.2 · Código de Conexión CREG',
    conectadoRed ? 'Se acuerda con el operador de red durante el trámite de conexión.' : 'No aplica.');

  const conteo = filas.reduce((a, f) => { a[f.estado] = (a[f.estado] || 0) + 1; return a; }, {});
  return { filas, conteo };
}

export default { calcularDespiece, checklistRetie, TIPO, DISTANCIAS_DEFECTO };
